// Stripe Connect Service for Contractor App
// V2 API - Updated for Stripe Connect V2

import { supabase } from '../utils/supabaseClient';

/**
 * The real reason an edge function failed.
 *
 * supabase.functions.invoke sets `error.message` to the generic
 * "Edge Function returned a non-2xx status code" and puts the SERVER's actual
 * message in `error.context`, which is a Response. So `error.message ||
 * fallback` can never reach its fallback -- the wrapper string is always
 * truthy -- and every caller here rendered that internal string straight into a
 * red banner. Two separate findings reported it as a user-facing defect on the
 * Connect and Products tabs before it was traced to this one line repeated at
 * six call sites.
 *
 * This is the same unwrap that createRefund already did inline (F-ATLAS-47-006);
 * it is lifted out so there is ONE of it rather than one per method.
 */
async function edgeErrorMessage(error, fallback) {
  const generic = /non-2xx status code/i.test(error?.message || '');
  if (!generic && error?.message) return error.message;
  try {
    const body = await error?.context?.json();
    if (body?.error) return body.error;
  } catch {
    // Non-JSON body (gateway error, timeout) -- fall through to the fallback,
    // which is the readable sentence the caller wanted all along.
  }
  return fallback;
}

const EDGE_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`;

// #3068: a payment "succeeded" when the money was CAPTURED. A later full or partial refund,
// or a dispute, does not un-capture it — counting those as failures dragged the rate down for
// every refund issued. failed / cancelled are unsuccessful; pending / processing are not decided
// yet and stay out of the rate until they are.
const CAPTURED_PAYMENT_STATUSES = ['completed', 'partially_completed', 'refunded', 'partially_refunded', 'disputed'];
const UNDECIDED_PAYMENT_STATUSES = ['pending', 'processing'];
export const summarizePayments = (rows) => {
  const decided = rows.filter((p) => !UNDECIDED_PAYMENT_STATUSES.includes(p.status));
  const captured = decided.filter((p) => CAPTURED_PAYMENT_STATUSES.includes(p.status)).length;
  return {
    totalPayments: rows.length,
    totalAmount: rows.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    decidedCount: decided.length,
    successRate: decided.length > 0 ? Math.round((captured / decided.length) * 100) : null,
  };
};

class StripeConnectService {
  constructor() {
    this.edgeFunctionUrl = EDGE_FUNCTION_URL;
  }

  /**
   * Create a Stripe Connect account and get onboarding URL (V2 API)
   */
  async startStripeOnboarding() {
    const { data, error } = await supabase.functions.invoke('stripe-connect-onboarding', {
      method: 'POST',
    });

    if (error) {
      console.error('Error starting Stripe onboarding:', error);
      throw new Error(await edgeErrorMessage(error, 'Could not start Stripe onboarding. Please try again.'));
    }

    return data;
  }

  /**
   * Get login link to Stripe dashboard (V2 API)
   */
  async getStripeDashboardLink() {
    const { data, error } = await supabase.functions.invoke('stripe-connect-dashboard', {
      method: 'POST',
    });

    if (error) {
      console.error('Error getting Stripe dashboard link:', error);
      throw new Error(await edgeErrorMessage(error, 'Could not open your Stripe dashboard. Please try again.'));
    }

    return data;
  }

  /**
   * Disconnect the company's Stripe Connect account. Board #2453.
   *
   * The function refuses while money is in flight and returns `blockers` saying which, so the
   * caller can tell the user WHY rather than just that it failed. Returns the raw payload for
   * that reason instead of throwing on a 409.
   */
  async disconnectStripe() {
    const { data, error } = await supabase.functions.invoke('stripe-connect-disconnect', {
      method: 'POST',
    });

    if (error) {
      // A refusal arrives as a non-2xx, which supabase-js surfaces as `error` — but the body
      // carries the reasons and is the useful part. edgeErrorMessage reads it.
      throw new Error(await edgeErrorMessage(error, 'Could not disconnect Stripe. Please try again.'));
    }

    return data;
  }

  /**
   * Get company's Stripe account status from V2 API directly
   */
  async getStripeAccountStatus(companyId) {
    // First check local database for existing data
    const { data: companyData, error: companyError } = await supabase
      .from('companies')
      .select('stripe_account_id, stripe_onboarding_complete')
      .eq('id', companyId)
      .single();

    if (companyError || !companyData?.stripe_account_id) {
      throw new Error('No Stripe account found. Please complete onboarding first.');
    }

    // Fetch fresh status from V2 API
    try {
      const { data: statusData, error: statusError } = await supabase
        .from('companies')
        .select('stripe_account_id')
        .eq('id', companyId)
        .single();

      if (statusError) throw statusError;

      // Call status endpoint to get fresh data from V2 API
      const response = await fetch(
        `${this.edgeFunctionUrl}/stripe-connect-status?company_id=${companyId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          return {
            ...companyData,
            ...result.data,
            source: 'api',
          };
        }
      }
    } catch (apiError) {
      console.warn('Could not fetch status from V2 API, using cached data:', apiError);
    }

    // Fall back to cached database data
    return {
      ...companyData,
      source: 'database',
    };
  }

  /**
   * Create a checkout session for customer payment (V2 API - Direct Charges)
   */
  async createCheckoutSession({ amount, companyId, customerId, jobId, invoiceId, jobTitle, description, customerEmail }) {
    // Amount should be in cents
    const amountInCents = Math.round(amount * 100);

    const { data, error } = await supabase.functions.invoke('stripe-create-checkout', {
      method: 'POST',
      body: {
        amount: amountInCents,
        companyId,
        customerId,
        jobId,
        invoiceId,
        jobTitle,
        description,
        customerEmail,
      },
    });

    if (error) {
      console.error('Error creating checkout session:', error);
      throw new Error(await edgeErrorMessage(error, 'Could not start the payment. Please try again.'));
    }

    return data;
  }

  /**
   * Get one page of payment history for a company.
   * Returns { rows, total } — total is the exact count for the same filter, so the caller
   * can paginate against it (board #1386: the footer used the page length as the total).
   */
  async getPaymentHistory(companyId, options = {}) {
    const { limit = 50, offset = 0, status, search } = options;

    // #3058: search is applied HERE, before .range(), so rows, paging and the cards all describe
    // the same set. It used to filter the fetched 25-row page in the browser, so a payment on
    // page 2 could not be found from page 1. PostgREST cannot OR across joined tables, so
    // customer / invoice / work-order matches are resolved to ids first.
    const searchOr = await this._paymentSearchFilter(companyId, search);

    const applyFilters = (q) => {
      let out = q.eq('company_id', companyId);
      if (status) out = out.eq('status', status);
      if (searchOr) out = out.or(searchOr);
      return out;
    };

    let query = applyFilters(
      supabase.from('payments').select('*, invoice:invoices(invoice_number)', { count: 'exact' })
    )
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching payment history:', error);
      throw new Error(await edgeErrorMessage(error, 'Could not load payment history. Please try again.'));
    }

    // Batch fetch work orders and customers (no FK on payments→work_orders)
    const jobIds = [...new Set((data || []).map(p => p.job_id).filter(Boolean))];
    const customerIds = [...new Set((data || []).map(p => p.customer_id).filter(Boolean))];

    const woMap = {};
    if (jobIds.length > 0) {
      const { data: workOrders } = await supabase
        .from('work_orders')
        .select('id, title, work_order_number')
        .in('id', jobIds);
      (workOrders || []).forEach(wo => { woMap[wo.id] = wo; });
    }

    const custMap = {};
    if (customerIds.length > 0) {
      const { data: customers } = await supabase
        .from('customers')
        .select('id, name, email')
        .in('id', customerIds);
      (customers || []).forEach(c => { custMap[c.id] = c; });
    }

    const rows = (data || []).map(payment => ({
      ...payment,
      work_order: woMap[payment.job_id] || null,
      customer: custMap[payment.customer_id] || null,
    }));
    // #3057: the cards describe the WHOLE filtered set, not the page. Two columns for every
    // matching payment is small next to the page query itself.
    const { data: allForStats } = await applyFilters(
      supabase.from('payments').select('amount, status')
    );
    return { rows, total: count ?? rows.length, stats: summarizePayments(allForStats || []) };
  }

  /** Build the or() filter for a Payment History search term, or null for no term. */
  async _paymentSearchFilter(companyId, search) {
    const term = String(search || '').trim();
    if (!term) return null;
    const safe = term.replace(/[%,()*]/g, ' ');
    const like = `%${safe}%`;
    const [cust, inv, wo] = await Promise.all([
      supabase.from('customers').select('id').or(`name.ilike.${like.replace(/%/g, '*')},email.ilike.${like.replace(/%/g, '*')}`).limit(500),
      supabase.from('invoices').select('id').eq('company_id', companyId).ilike('invoice_number', like).limit(500),
      supabase.from('work_orders').select('id').eq('company_id', companyId)
        .or(`work_order_number.ilike.${like.replace(/%/g, '*')},title.ilike.${like.replace(/%/g, '*')}`).limit(500),
    ]);
    const star = `*${safe}*`;
    const parts = [
      `stripe_payment_intent_id.ilike.${star}`,
      `transaction_id.ilike.${star}`,
    ];
    const ids = (r) => (r.data || []).map((x) => x.id);
    if (ids(cust).length) parts.push(`customer_id.in.(${ids(cust).join(',')})`);
    if (ids(inv).length) parts.push(`invoice_id.in.(${ids(inv).join(',')})`);
    if (ids(wo).length) parts.push(`job_id.in.(${ids(wo).join(',')})`);
    const numeric = term.replace(/[$,\s]/g, '');
    if (/^\d+(\.\d{1,2})?$/.test(numeric)) parts.push(`amount.eq.${Number(numeric)}`);
    return parts.join(',');
  }

  /**
   * Get single payment details
   */
  async getPaymentDetails(paymentId) {
    const { data: payment, error } = await supabase
      .from('payments')
      .select(`
        *,
        invoice:invoices(invoice_number, total_amount)
      `)
      .eq('id', paymentId)
      .single();

    if (error) {
      console.error('Error fetching payment:', error);
      throw new Error(await edgeErrorMessage(error, 'Could not load that payment. Please try again.'));
    }

    // Fetch customer separately and merge with correct alias
    if (payment?.customer_id) {
      const { data: customer } = await supabase
        .from('customers')
        .select('name, email, phone')
        .eq('id', payment.customer_id)
        .single();
      payment.customer = customer || null;
    }

    return payment;
  }

  /**
   * Format amount for display
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  /**
   * Get Stripe onboarding refresh URL
   */
  getStripeRefreshUrl() {
    return `${window.location.origin}/settings?tab=payments&stripe=refresh`;
  }

  /**
   * Get Stripe success URL
   */
  getStripeSuccessUrl() {
    return `${window.location.origin}/settings?tab=payments&stripe=success`;
  }

  /**
   * Redirect to Stripe Checkout
   */
  redirectToCheckout(sessionUrl) {
    if (sessionUrl) {
      window.location.href = sessionUrl;
    } else {
      throw new Error('No checkout URL provided');
    }
  }

  /**
   * Issue a refund (full or partial) for a payment
   * @param {string} paymentId - DB payment record ID
   * @param {number} amount - Refund amount in dollars (omit for full refund)
   * @param {string} reason - 'duplicate' | 'fraudulent' | 'requested_by_customer' | custom text
   * @param {string} [invoiceId] - refund this invoice's share of the payment (payment_applications);
   *   omit to refund the payment as a whole
   */
  async createRefund({ paymentId, invoiceId, amount, reason, notes }) {
    const { data, error } = await supabase.functions.invoke('stripe-create-refund', {
      method: 'POST',
      body: {
        paymentId,
        invoiceId: invoiceId || undefined,
        amount: amount ? Math.round(amount * 100) : undefined, // Convert to cents, omit for full
        reason: reason || 'requested_by_customer',
        notes: notes || null,
      },
    });

    if (error) {
      // Was the inline original of edgeErrorMessage (F-ATLAS-47-006). Folded into
      // the shared helper so the unwrap has one home; behaviour is unchanged
      // except that a non-JSON body now reaches a readable fallback instead of
      // the "non-2xx status code" wrapper.
      throw new Error(await edgeErrorMessage(error, 'Could not create the refund. Please try again.'));
    }
    if (!data?.success) throw new Error(data?.error || 'Refund failed');

    return data.data; // { refundId, amount, isPartial, status }
  }
}

// Export singleton instance
const stripeConnectService = new StripeConnectService();
export default stripeConnectService;
